import { createTheme } from '@mui/material/styles';

const fontFamilyHeading = '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const fontFamilyBody = '"Barlow", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const lightPalette = {
  mode: 'light',
  primary: {
    main: '#ed7b07',
    light: '#f59a3d',
    dark: '#c96506',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#519DC8',
    light: '#7ab4d9',
    dark: '#3d7a9e',
    contrastText: '#ffffff',
  },
  background: {
    default: '#f8f9fa',
    paper: '#ffffff',
  },
  text: {
    primary: '#1a1a1a',
    secondary: '#6c757d',
  },
  divider: 'rgba(0, 0, 0, 0.12)',
};

const darkPalette = {
  mode: 'dark',
  primary: {
    main: '#ed7b07',
    light: '#f59a3d',
    dark: '#c96506',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#519DC8',
    light: '#7ab4d9',
    dark: '#3d7a9e',
    contrastText: '#ffffff',
  },
  background: {
    default: '#0E0E0E',
    paper: '#1a1a1a',
  },
  text: {
    primary: 'rgba(255, 255, 255, 0.92)',
    secondary: 'rgba(255, 255, 255, 0.70)',
  },
  divider: 'rgba(255, 255, 255, 0.12)',
};

export function createShowtimeTheme(mode = 'light') {
  const isDark = mode === 'dark';
  const palette = isDark ? darkPalette : lightPalette;

  return createTheme({
    palette,
    typography: {
      fontFamily: fontFamilyBody,
      h1: { fontFamily: fontFamilyHeading, fontWeight: 800 },
      h2: { fontFamily: fontFamilyHeading, fontWeight: 800 },
      h3: { fontFamily: fontFamilyHeading, fontWeight: 700 },
      h4: { fontFamily: fontFamilyHeading, fontWeight: 600 },
      h5: { fontFamily: fontFamilyHeading, fontWeight: 600 },
      h6: { fontFamily: fontFamilyHeading, fontWeight: 600 },
      button: { fontFamily: fontFamilyHeading, fontWeight: 600 },
    },
    shape: {
      borderRadius: 10,
    },
    transitions: {
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 10,
            fontWeight: 600,
          },
          containedPrimary: {
            boxShadow: '0 2px 12px rgba(237, 123, 7, 0.35)',
            '&:hover': {
              boxShadow: '0 4px 16px rgba(237, 123, 7, 0.45)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: isDark
              ? '0 2px 8px rgba(0, 0, 0, 0.3)'
              : '0 2px 12px rgba(0, 0, 0, 0.08)',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: isDark
              ? '0 2px 8px rgba(0, 0, 0, 0.3)'
              : '0 2px 12px rgba(0, 0, 0, 0.08)',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : 'none',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'small',
        },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 10,
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderWidth: 2,
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            borderBottom: isDark
              ? '1px solid rgba(255, 255, 255, 0.08)'
              : '1px solid rgba(0, 0, 0, 0.08)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: isDark
              ? '1px solid rgba(255, 255, 255, 0.08)'
              : '1px solid rgba(0, 0, 0, 0.08)',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            margin: '2px 8px',
            '&.Mui-selected': {
              backgroundColor: isDark
                ? 'rgba(237, 123, 7, 0.15)'
                : 'rgba(237, 123, 7, 0.12)',
              '&:hover': {
                backgroundColor: isDark
                  ? 'rgba(237, 123, 7, 0.2)'
                  : 'rgba(237, 123, 7, 0.16)',
              },
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              fontWeight: 600,
              fontFamily: fontFamilyHeading,
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: isDark
              ? '1px solid rgba(255, 255, 255, 0.08)'
              : '1px solid rgba(0, 0, 0, 0.08)',
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: 'none',
          },
          columnHeaders: {
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
          },
          row: {
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
            },
          },
        },
      },
    },
  });
}
